"use client";

import { useState, type FormEvent } from "react";

type Status = "idle" | "submitting" | "success" | "error";

// The site is a static export (no server), so the form posts directly to the
// existing backend that the original goldenitc.sa site used — an Express
// endpoint on Vercel that emails the inquiry. Overridable at build time.
const CONTACT_ENDPOINT =
  process.env.NEXT_PUBLIC_CONTACT_ENDPOINT || "https://goldenitc.vercel.app/contact";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError("");

    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot — real users leave this empty. Pretend success so bots get no
    // signal, and never hit the backend.
    if (String(data.get("company") ?? "").trim()) {
      form.reset();
      setStatus("success");
      return;
    }

    // The backend expects { name, email, subject, message }.
    const payload = {
      name: String(data.get("fullName") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      subject: String(data.get("subject") ?? "").trim(),
      message: String(data.get("message") ?? "").trim(),
    };

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        setError("Something went wrong. Please try again or email us directly.");
        setStatus("error");
        return;
      }

      form.reset();
      setStatus("success");
    } catch {
      setError("Network error. Please check your connection and try again.");
      setStatus("error");
    }
  }

  const fieldClass =
    "mt-1.5 w-full rounded-lg border border-line bg-white px-4 py-2.5 text-ink outline-none transition-colors placeholder:text-muted/70 focus:border-gold focus:ring-2 focus:ring-gold/30";
  const labelClass = "text-sm font-medium text-ink";

  if (status === "success") {
    return (
      <div className="card flex flex-col items-center p-8 text-center md:p-10">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gold-soft text-gold-dark">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="mt-5 text-xl font-bold text-ink">Message sent</h3>
        <p className="mt-2 text-muted">
          Thanks for reaching out — our team has received your message and will get back to you
          shortly.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="btn btn-ink mt-6"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 md:p-8" noValidate>
      <p className="eyebrow">Send Us a Message</p>
      <h3 className="mt-3 text-2xl font-bold text-ink">Let&apos;s talk about your project</h3>

      {/* Honeypot — hidden from users, catches bots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>
          Company
          <input type="text" name="company" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="fullName" className={labelClass}>Full Name</label>
          <input id="fullName" name="fullName" type="text" required autoComplete="name" className={fieldClass} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="email" className={labelClass}>Email</label>
          <input id="email" name="email" type="email" required autoComplete="email" className={fieldClass} placeholder="you@example.com" />
        </div>
        <div>
          <label htmlFor="subject" className={labelClass}>Subject</label>
          <input id="subject" name="subject" type="text" required className={fieldClass} placeholder="How can we help?" />
        </div>
        <div>
          <label htmlFor="message" className={labelClass}>Message</label>
          <textarea id="message" name="message" rows={5} required className={`${fieldClass} resize-y`} placeholder="Tell us about your project…" />
        </div>
      </div>

      {status === "error" && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="btn btn-gold mt-6 w-full disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === "submitting" ? "Sending…" : "Send Message"}
      </button>

      <p className="mt-3 text-center text-xs text-muted">
        We&apos;ll get back to you soon and never share your details.
      </p>
    </form>
  );
}
